import {
    Hash,
    type LucideIcon,
    Megaphone,
    MessageSquare,
    PanelRight,
    Pencil,
    Phone,
    PhoneForwarded,
    RefreshCw,
    ThumbsDown,
    ThumbsUp,
    Zap,
} from 'lucide-react';

import { SOURCE_LABELS } from '@apps/web/features/admin_agent_notes/ui/note_status';
import { cn } from '@apps/web/lib/utils';
import { type components } from '@libraries/typescript/api_client';

type AgentNoteSource = components['schemas']['AgentNoteSource'];
type RatingLabel = components['schemas']['RatingLabel'];

function iconFor(
    source: AgentNoteSource,
    rating: RatingLabel | null | undefined,
): LucideIcon {
    if (source === 'feedback' && rating === 'bad') {
        return ThumbsDown;
    }
    return SOURCE_ICONS[source];
}

function labelFor(
    source: AgentNoteSource,
    rating: RatingLabel | null | undefined,
): string {
    if (source === 'feedback' && rating) {
        return `${SOURCE_LABELS[source]} (${rating})`;
    }
    return SOURCE_LABELS[source];
}

const SOURCE_ICONS: Record<AgentNoteSource, LucideIcon> = {
    sms: MessageSquare,
    voice: Phone,
    voice_post_call: PhoneForwarded,
    sidebar: PanelRight,
    slack: Hash,
    outreach: Megaphone,
    automation: Zap,
    ui: Pencil,
    sync: RefreshCw,
    feedback: ThumbsUp,
};

/** Source glyph + label. */
export function SourceBadge({
    source,
    rating,
    className,
}: {
    readonly source: AgentNoteSource;
    readonly rating?: RatingLabel | null;
    readonly className?: string;
}) {
    const Icon = iconFor(source, rating);
    return (
        <span
            className={cn(
                'text-muted-foreground inline-flex items-center gap-1.5 text-xs whitespace-nowrap',
                className,
            )}
        >
            <Icon aria-hidden="true" className="h-3.5 w-3.5" />
            {labelFor(source, rating)}
        </span>
    );
}

/** Icon-only source marker; the label lives in the tooltip. */
export function SourceGlyph({
    source,
    rating,
    className,
}: {
    readonly source: AgentNoteSource;
    readonly rating?: RatingLabel | null;
    readonly className?: string;
}) {
    const Icon = iconFor(source, rating);
    const label = labelFor(source, rating);
    return (
        <span
            className={cn('text-muted-foreground inline-flex', className)}
            title={label}
            aria-label={label}
        >
            <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        </span>
    );
}
